<template>
  <div
    class="combobox-filter-inline-label"
    @keydown="keydownHandler"
  >
    <v-select
      :class="[
        shiftIsPressed ? 'v-select--shift-is-pressed' : '',
        valueArr && valueArr.length ? 'v-select--has-selection' : '',
        !isBlured ? 'v-select--label-is-search' : ''
      ]"
      ref="combobox2000"
      v-model="valueArr"
      multiple
      v-bind="attrsWithoutItems"
      :items="items"
      :cache-items="false"
      @change="onChange"
      @click:append="() => Clear()"
      :allow-overflow="false"
      return-object
      append-icon="mdi-close"
      @keyup="keyupHandler"
      @paste="onPaste"
      @copy="onCopy"
      v-on:blur="onBlur"
      @focus="onFocus"
      :menu-props="computedMenuProps"
    >
      <template v-slot:label>
        <span v-if="isBlured">{{ $attrs.label }}</span>
        <span v-else class="combobox-filter-search-text">
          {{ selectionLabel }}{{ localSearch }}<span v-if="localSearch" class="combobox-filter-caret" aria-hidden="true"></span>
        </span>
      </template>
      <template v-slot:selection="{ item, index }">
        <div class="combobox-filter-selection-item">
          <span v-if="index < displayCount">
            {{
              $props.translate
                ? $props.localizationFunc(item)
                : item[$attrs.itemText]
            }}
          </span>
          <span v-if="index === displayCount">
            (+{{ $t("nOthers", [valueArr.length - index]) }})
          </span>
        </div>
      </template>
      <template v-slot:item="{ item }">
        <span v-if="$props.translate">{{ $props.localizationFunc(item) }}</span>
        <span v-else>{{ item[$attrs.itemText] }}</span>
      </template>
    </v-select>
  </div>
</template>

<script>
export default {
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'valueEv',
  },
  props: {
    value: Array,
    translate: {
      type: Boolean,
      default: false,
    },
    localizationFunc: {
      type: Function,
    },
    displayCount: {
      type: Number,
      default: 1,
    },
    searchFunction: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      valueArr: [],
      shiftIsPressed: false,
      shiftKeyHeld: false,
      shiftFirstIndex: -1,
      shiftSecondIndex: -1,
      lastActiveDate: null,
      searchFuncRunned: false,
      activeStatuses: ['waiting', 'process', 'pending', 'error'],
      localSearch: '',
      isBlured: true,
      searchArr: [],
    };
  },
  mounted() {
    this.valueArr = this.$props.value ?? [];
    document.addEventListener('keydown', this.shiftKeyDownHandler);
    document.addEventListener('keyup', this.shiftKeyUpHandler);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.shiftKeyDownHandler);
    document.removeEventListener('keyup', this.shiftKeyUpHandler);
  },
  computed: {
    attrsWithoutItems() {
      const rest = { ...this.$attrs };

      delete rest.items;

      return rest;
    },
    computedMenuProps() {
      return {
        closeOnClick: false,
        closeOnContentClick: false,
        offsetY: true,
        nudgeBottom: 1
      };
    },
    items() {
      const itemText = this.$attrs.itemText || 'name';
      const getText = (item) => {
        if (item == null) return '';
        if (this.translate && this.localizationFunc) return String(this.localizationFunc(item));
        if (typeof item === 'object') return String(item[itemText] ?? '');

        return String(item);
      };

      let fullList;
      if (this.$attrs.items && this.$attrs.items.length > 0) {
        fullList = this.$attrs.items;
      } else if (this.valueArr && this.valueArr.length > 0) {
        fullList = this.valueArr;
      } else {
        fullList = this.searchArr || [];
      }

      const q = (this.localSearch || '').toLowerCase().trim();

      if (!q) {

        return fullList;
      }

      return fullList.filter((item) =>
        getText(item).toLowerCase().includes(q)
      );
    },
    allActiveIsSelected() {
      const activeStatusesCount = this.$attrs.items.filter((item) =>
        this.activeStatuses.some((statusName) =>
          item.statusName.toLowerCase().includes(statusName)
        )
      ).length;

      const selectedActiveStatusesCount = this.valueArr.filter((selectedItem) =>
        this.activeStatuses.some((statusName) =>
          selectedItem.statusName.toLowerCase().includes(statusName)
        )
      ).length;

      return (
        activeStatusesCount === selectedActiveStatusesCount &&
        !this.allIsSelected
      );
    },
    allIsSelected() {
      return this.$attrs.items.length === this.valueArr.length;
    },
    selectionLabel() {
      if (!this.valueArr || this.valueArr.length === 0) return '';
      const itemText = this.$attrs.itemText || 'name';
      const getText = (item) => {
        if (item == null) return '';
        if (this.translate && this.localizationFunc) return String(this.localizationFunc(item));
        if (typeof item === 'object') return String(item[itemText] ?? '');

        return String(item);
      };
      const n = this.displayCount || 1;
      const parts = this.valueArr.slice(0, n).map(getText);
      const rest = this.valueArr.length - n;

      return rest > 0 ? parts.join(', ') + ` (+${rest}) ` : parts.join(', ') + ' ';
    },
  },
  watch: {
    localSearch() {
      this.lastActiveDate = Date.now();

      if (!this.searchFuncRunned) {
        this.searchFuncRunned = true;
        setTimeout(this.runSearch, 500);
      }
    },
    value() {
      this.valueArr = this.value;
    },
  },
  methods: {
    async runSearch() {
      let dif;
      do {
        dif = Date.now() - this.lastActiveDate;
      } while (dif < 500);

      this.searchFuncRunned = false;

      if (!this.localSearch || this.localSearch.length === 0) {
        this.searchArr = [];
      } else if (this.searchFunction) {
        const result = await this.searchFunction(this.localSearch);
        this.searchArr = Array.isArray(result) ? result : [].concat(result || []);
      }
    },
    onFocus() {
      this.isBlured = false;
      if (this.valueArr && this.valueArr.length > 0) {
        this.localSearch = '';
        this.$nextTick(() => {
          if (this.$refs.combobox2000) {
            this.$refs.combobox2000.isMenuActive = true;
          }
        });
      }
    },
    onBlur() {
      this.$emit('valueEv', this.valueArr);
      this.isBlured = true;
      this.localSearch = '';
    },
    Clear() {
      this.valueArr = [];
      this.isBlured = true;
      this.localSearch = '';
      this.$emit('valueEv', this.valueArr);
    },
    onChange(val) {
      const visibleItems = this.items;
      if (this.shiftKeyHeld && visibleItems && visibleItems.length && val.length >= 2) {
        const itemValue = this.$attrs.itemValue;
        const startItem = val[val.length - 2];
        const endItem = val[val.length - 1];

        const findIndex = (list, obj) => {
          if (itemValue != null) {
            const key = typeof obj === 'object' ? obj[itemValue] : obj;

            return list.findIndex((item) => (typeof item === 'object' ? item[itemValue] : item) === key);
          }

          return list.indexOf(obj);
        };

        let startIdx = findIndex(visibleItems, startItem);
        let endIdx = findIndex(visibleItems, endItem);

        if (startIdx === -1) startIdx = endIdx;
        if (endIdx === -1) endIdx = startIdx;

        const from = Math.min(startIdx, endIdx);
        const to = Math.max(startIdx, endIdx);
        const range = visibleItems.slice(from, to + 1);

        this.valueArr = range;
        this.$emit('input', range);
        this.localSearch = '';
        this.$nextTick(() => {
          if (this.$refs.combobox2000) this.$refs.combobox2000.isMenuActive = true;
        });

        return;
      }

      this.$emit('input', val);
      this.localSearch = '';
      this.$nextTick(() => {
        if (this.$refs.combobox2000) this.$refs.combobox2000.isMenuActive = true;
      });
    },
    shiftKeyDownHandler(event) {
      if (event.key === 'Shift' && !this.shiftKeyHeld) {
        this.shiftKeyHeld = true;
      }
    },
    shiftKeyUpHandler(event) {
      if (event.key === 'Shift' && this.shiftKeyHeld) {
        this.shiftKeyHeld = false;
      }
    },
    keydownHandler(event) {
      if (event.key === 'Shift' && !this.shiftIsPressed) {
        this.shiftIsPressed = true;
      }

      const menuOpen = this.$refs.combobox2000 && this.$refs.combobox2000.isMenuActive;
      const inSearchMode = !this.isBlured || menuOpen;

      if (inSearchMode) {
        if (event.key === 'Backspace') {
          event.preventDefault();
          event.stopPropagation();
          this.isBlured = false;
          if (this.localSearch && this.localSearch.length > 0) {
            this.localSearch = this.localSearch.slice(0, -1);
          } else if (this.valueArr && this.valueArr.length > 0) {
            this.valueArr = this.valueArr.slice(0, -1);
            this.$emit('valueEv', this.valueArr);
            this.$emit('input', this.valueArr);
          }
        } else if (
          event.key.length === 1 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey &&
          !['Enter', 'Escape', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
        ) {
          event.preventDefault();
          event.stopPropagation();
          this.isBlured = false;
          this.localSearch = (this.localSearch || '') + event.key;
        }
      }
    },
    keyupHandler(event) {
      if (event.key === 'Shift' && this.shiftIsPressed) {
        this.shiftIsPressed = false;
      }
    },
    onPaste(event) {
      if (this.isBlured) return;
      event.preventDefault();
      event.stopPropagation();
      this.isBlured = false;
      const text = (event.clipboardData || window.clipboardData).getData('text');
      if (text) this.localSearch = (this.localSearch || '') + text;
    },
    onCopy(event) {
      if (this.isBlured) return;
      const text = this.localSearch || '';
      if (text && event.clipboardData) {
        event.preventDefault();
        event.clipboardData.setData('text/plain', text);
      }
    },
  },
};
</script>
<style>
.darkBackground {
  background: #363636;
  color: white;
}
.v-list-item {
  min-height: fit-content;
}
.v-select--shift-is-pressed {
  font-weight: bold;
}
.v-select--has-selection:not(.v-select--label-is-search) .v-label {
  display: none;
}
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selection,
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selections,
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selection *,
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selections * {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
}
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-label {
  z-index: 1;
  position: relative;
  pointer-events: none;
}
.combobox-filter-inline-label .v-select.v-select--label-is-search input {
  caret-color: transparent;
  color: transparent;
}
.combobox-filter-inline-label .v-select .v-label {
  top: 50% !important;
  transform: translateY(-50%);
}
/* Обрезка длинного ввода: одна строка, многоточие*/
.combobox-filter-inline-label {
  min-width: 0;
}
.combobox-filter-inline-label .v-input__slot {
  min-width: 0 !important;
}
.combobox-filter-inline-label .v-select__selections {
  flex-wrap: nowrap !important;
  min-width: 0;
  overflow: hidden !important;
}
.combobox-filter-inline-label .v-select__selection,
.combobox-filter-inline-label .combobox-filter-selection-item,
.combobox-filter-inline-label .v-select__selections > *,
.combobox-filter-inline-label .v-select__selection > * {
  overflow: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap !important;
  min-width: 0;
}
.combobox-filter-inline-label .v-select .v-label,
.combobox-filter-inline-label .v-select .v-label * {
  overflow: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap !important;
  min-width: 0;
}
.combobox-filter-search-text {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  min-width: 0;
}
.combobox-filter-caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 1px;
  vertical-align: text-bottom;
  background: currentColor;
  animation: combobox-filter-caret-blink 1s step-end infinite;
}
@keyframes combobox-filter-caret-blink {
  50% {
    opacity: 0;
  }
}
</style>
